// Audio playback helpers.  Safari blocks audio.play() outside a user
// gesture, so unlockAudio() must be called synchronously inside the
// click/keydown handler BEFORE any await.  See frontend/app.js for the
// original notes — the React port keeps the same shape.

export const SILENT_MP3 =
  "data:audio/mpeg;base64," +
  "//uQxAAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV" +
  "VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV" +
  "VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV" +
  "VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV" +
  "VVVVVVVVVVQ=";

let _ambientFadeTimer: number | null = null;

export function fadeAudioOut(
  el: HTMLAudioElement,
  durationMs: number,
  onDone?: () => void,
): void {
  if (_ambientFadeTimer != null) clearInterval(_ambientFadeTimer);
  const steps = 20;
  const interval = durationMs / steps;
  const startVol = el.volume;
  let step = 0;
  const timer = window.setInterval(() => {
    step++;
    el.volume = Math.max(0, startVol * (1 - step / steps));
    if (step >= steps) {
      clearInterval(timer);
      el.volume = 0;
      if (onDone) onDone();
    }
  }, interval);
  _ambientFadeTimer = timer;
}

export function fadeAudioIn(el: HTMLAudioElement, durationMs: number): void {
  if (_ambientFadeTimer != null) clearInterval(_ambientFadeTimer);
  const steps = 20;
  const interval = durationMs / steps;
  let step = 0;
  const timer = window.setInterval(() => {
    step++;
    el.volume = Math.min(1, step / steps);
    if (step >= steps) {
      clearInterval(timer);
      el.volume = 1;
    }
  }, interval);
  _ambientFadeTimer = timer;
}

// Issue #372: short per-turn SFX, layered over ambient (no ducking).
// Pitch variation: we randomise playbackRate in [0.92, 1.08] (~+/-1.4
// semitones) and explicitly set preservesPitch=false so the speed
// change pulls the pitch with it. Without preservesPitch=false the
// browser keeps the original pitch and the effect is inaudible.
export function playSfx(el: HTMLAudioElement | null, url: string | null | undefined): void {
  if (!el || !url) return;
  try {
    el.src = url;
    // preservesPitch is non-standard but supported; cast around the
    // missing TS type.
    (el as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = false;
    el.playbackRate = 0.92 + Math.random() * 0.16;
    el.volume = 0.8;
    el.play().catch(() => {
      /* autoplay policy may reject — silent failure */
    });
  } catch {
    /* no-op */
  }
}
